import { forwardRef } from 'react'
import { motion } from 'framer-motion'

import MicroVolumeStimulusTypeIcon from 'components/Program/MicroVolumeConfig/MicroVolumeStimulusTypeIcon'
import MicroVolumeConfigEditor from 'components/Program/MicroVolumeConfig/MicroVolumeConfigEditor'

const MicroVolumeConfigBar = forwardRef(function MicroVolumeConfigBar({ 
    isSelected = false,
    enableAnimation = true,
    enableHoverOutline = false,
    macroVolumeFocusTypeId,
    macroVolumeFocusTypeHashMap,
    microVolumeConfig,
    microVolumeConfigIndex = null,
    microVolumeStimulusTypeHashMap,
    onClick = null,
    onDuplicate = null,
    onDelete = null,
    variant = 'default',
}, ref) {

    const isTimelineVariant = variant === 'timeline'
    const isEditVariant = variant === 'edit'
    const macroFocusTypeColor = macroVolumeFocusTypeHashMap?.[macroVolumeFocusTypeId]?.colorHex
    const iconKey = microVolumeStimulusTypeHashMap?.[microVolumeConfig.microVolumeStimulusTypeId]?.iconKey

    const GetWeekBarStyle = () => {
        const taperLevel = microVolumeConfig.taperLevel

        var style = 'flex items-end rounded transition-shadow '

        if (isTimelineVariant) {
            style += 'w-full ring-offset-2 ring-offset-white '
        } else if (isEditVariant) {
            style += 'w-20 ring-offset-2 ring-offset-zinc-700 '
        } else {
            style += 'w-12 ring-offset-2 ring-offset-zinc-700 '
        }

        if (isEditVariant) {
            if (taperLevel === 'taper-none') style += 'h-48 '
            if (taperLevel === 'taper-one') style += 'h-36 '
            if (taperLevel === 'taper-two') style += 'h-24 '
            if (taperLevel === 'taper-three') style += 'h-12 '
        } else {
            if (taperLevel === 'taper-none') style += 'h-28 '
            if (taperLevel === 'taper-one') style += 'h-20 '
            if (taperLevel === 'taper-two') style += 'h-14 '
            if (taperLevel === 'taper-three') style += 'h-8 '
        }

        if (onClick !== null) style += 'cursor-pointer '

        if (isSelected) style += 'ring-2 ring-white '
        else if (onClick !== null || enableHoverOutline) style += 'group-hover:ring-2 group-hover:ring-white '

        return style
    }

    const GetBarContainerStyle = () => {
        if (isEditVariant) return 'h-48 group relative flex flex-col items-center justify-end'
        return 'h-32 group relative flex flex-col items-center justify-end'
    }

    const barContent = (
        <div className={isEditVariant ? 'w-80 space-y-4' : 'space-y-4'}>
            <div
                className={GetBarContainerStyle()}
                onClick={onClick}
            >
                <div 
                    className={GetWeekBarStyle()}
                    style={{ 
                        backgroundColor: macroFocusTypeColor 
                    }}
                >
                    {iconKey &&
                        <div className='h-8 p-1 w-full flex items-center justify-center'>
                            <MicroVolumeStimulusTypeIcon iconKey={iconKey} />
                        </div>
                    }
                </div>
            </div>

            {isEditVariant && microVolumeConfigIndex !== null &&
                <MicroVolumeConfigEditor
                    microVolumeConfigIndex={microVolumeConfigIndex}
                    microVolumeConfig={microVolumeConfig}
                    DuplicateSelectedMicroVolumeConfig={onDuplicate}
                    DeleteSelectedMicroVolumeConfig={onDelete}
                />
            }
        </div>
    )

    if (isTimelineVariant) {
        return (
            <div 
                ref={ref} 
                className='w-full'
            >
                {barContent}
            </div>
        )
    }

    const wrapperClassName = isEditVariant ? 'shrink-0 w-80' : 'h-32 shrink-0'

    if (!enableAnimation) {
        return (
            <div 
                ref={ref} 
                className={wrapperClassName}
            >
                {barContent}
            </div>
        )
    }

    return (
        <motion.div
            ref={ref}
            layout
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={wrapperClassName}
        >
            {barContent}
        </motion.div>
    )
})

export default MicroVolumeConfigBar
